import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Loader2, Send, Sparkles } from "lucide-react";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const SEL = "h-9 w-full rounded-md border bg-background px-2 text-sm";

export function MailPreview({ from, to, subject, body }) {
  const { t } = useI18n();
  return (
    <div className="rounded-lg border text-sm" data-testid="mail-preview">
      <div className="px-4 py-2 border-b text-xs space-y-0.5 bg-muted/40">
        <div><span className="text-muted-foreground">{t("From")} :</span> <span translate="no">{from}</span></div>
        <div><span className="text-muted-foreground">{t("To")} :</span> <span translate="no" data-testid="mail-preview-to">{to}</span></div>
        <div className="font-semibold text-sm pt-1" data-testid="mail-preview-subject">{subject}</div>
      </div>
      <div className="px-4 py-3 whitespace-pre-wrap" data-testid="mail-preview-body">{body}</div>
    </div>
  );
}

export function EmailComposer({ candidate, open, onOpenChange, onSent }) {
  const { setUpgradeOpen } = useAuth();
  const { t } = useI18n();
  const emails = candidate.contacts.filter((c) => c.type.includes("email") && c.permitted_use === "recruiting_outreach");
  const [status, setStatus] = useState(null);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ contact_id: emails[0]?.id || "", account_id: "", project_id: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [sig, setSig] = useState("");
  const [tpls, setTpls] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const { user } = useAuth();
  const fill = (s) => {
    const p = projects.find((x) => x.id === f.project_id);
    const vars = { "prénom": candidate.full_name.split(" ")[0], nom: candidate.full_name, poste: p?.title || candidate.current_title || "",
      entreprise: candidate.current_company || "", nom_recruteur: user?.full_name || "", entreprise_recruteur: user?.memberships?.[0]?.organization_name || "" };
    return s.replace(/\{([a-zé_]+)\}/gi, (m, k) => vars[k] ?? m);
  };
  const applyTpl = (id) => { const tpl = tpls.find((x) => x.id === id); if (tpl) setF((x) => ({ ...x, subject: fill(tpl.subject), body: fill(tpl.body) })); };
  const aiDraft = async () => {
    setDrafting(true);
    try { const { data } = await api.post("/mail/draft", { candidate_id: candidate.id, project_id: f.project_id || null }); setF((x) => ({ ...x, subject: data.subject, body: data.body })); }
    catch (e) { toast.error(errorDetail(e)); } finally { setDrafting(false); }
  };
  useEffect(() => {
    if (!open) return;
    api.get("/mail/status").then((r) => { setStatus(r.data); setF((x) => ({ ...x, account_id: r.data.accounts.find((a) => a.status === "connected")?.id || "" })); });
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/mail/templates").then((r) => setTpls(r.data));
    api.get("/mail/signature").then((r) => setSig(r.data.signature));
    setPreview(false);
  }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const accounts = (status?.accounts || []).filter((a) => a.status === "connected");
  const send = async () => {
    if (!status?.can_send) return setUpgradeOpen(true);
    setBusy(true);
    try {
      const { data } = await api.post("/mail/send", { ...f, candidate_id: candidate.id, project_id: f.project_id || null, account_id: f.account_id || null });
      toast.success(t("Email sent")); onSent?.(data); onOpenChange(false);
    } catch (e) { if (errorCode(e) === "upgrade_required") setUpgradeOpen(true); else toast.error(errorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="email-composer">
        <DialogHeader><DialogTitle>{t("Email {{name}}", { name: candidate.full_name })}</DialogTitle><DialogDescription className="sr-only">{t("Sent from your own mailbox. Replies are tracked automatically in Messages.")}</DialogDescription></DialogHeader>
        {!emails.length ? <p className="text-sm text-muted-foreground" data-testid="composer-no-contact">{t("No email on record permits recruiting outreach for this candidate.")}</p>
          : status && !accounts.length ? <p className="text-sm text-muted-foreground" data-testid="composer-no-mailbox">{t("Connect your mailbox first in")} <Link to="/app/messages" className="text-primary underline">{t("Messages")}</Link>.</p>
          : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs space-y-1"><span className="text-muted-foreground">{t("From")}</span>
                  <select className={SEL} value={f.account_id} onChange={set("account_id")} data-testid="composer-account-select">{accounts.map((a) => <option key={a.id} value={a.id}>{a.address}</option>)}</select></label>
                <label className="text-xs space-y-1"><span className="text-muted-foreground">{t("To")}</span>
                  <select className={SEL} value={f.contact_id} onChange={set("contact_id")} data-testid="composer-contact-select">{emails.map((c) => <option key={c.id} value={c.id}>{c.value || t("{{type}} (masked)", { type: t(c.type.replace("_", " ")) })}</option>)}</select></label>
              </div>
              <label className="text-xs space-y-1 block"><span className="text-muted-foreground">{t("Link to project (moves the candidate to “Contacted”)")}</span>
                <select className={SEL} value={f.project_id} onChange={set("project_id")} data-testid="composer-project-select"><option value="">{t("None")}</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select></label>
              <div className="flex flex-wrap gap-2 items-center">
                <select className={`${SEL} flex-1`} defaultValue="" onChange={(e) => applyTpl(e.target.value)} data-testid="composer-template-select"><option value="">{t("Use a template…")}</option>{tpls.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}</select>
                <Button type="button" size="sm" variant="outline" onClick={aiDraft} disabled={drafting} data-testid="composer-ai-draft">{drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-primary" />}{t("Write with AI")}</Button>
              </div>
              <Input placeholder={t("Subject")} value={f.subject} onChange={set("subject")} data-testid="composer-subject" />
              <Textarea rows={8} placeholder={t("Write your message…")} value={f.body} onChange={set("body")} data-testid="composer-body" />
              <p className="text-[11px] text-muted-foreground">{t("Sent from your own mailbox. Replies are tracked automatically in Messages.")} {t("Your signature (Messages → Mailbox) is added automatically.")}</p>
              <Button className="w-full" disabled={!f.subject.trim() || !f.body.trim() || !f.contact_id} onClick={() => setPreview(true)} data-testid="composer-preview">
                <Eye className="w-4 h-4" />{t("Preview")}
              </Button>
            </div>
          )}
        {preview && (
          <div className="absolute inset-0 bg-background rounded-lg p-6 flex flex-col gap-3 overflow-y-auto" data-testid="composer-preview-pane">
            <div className="text-lg font-semibold">{t("Preview")}</div>
            <MailPreview from={accounts.find((a) => a.id === f.account_id)?.address} to={emails.find((c) => c.id === f.contact_id)?.value || candidate.full_name}
              subject={f.subject} body={sig ? `${f.body.trimEnd()}\n\n-- \n${sig}` : f.body} />
            <div className="flex gap-2 mt-auto">
              <Button variant="outline" className="flex-1" onClick={() => setPreview(false)} data-testid="composer-back">{t("Edit")}</Button>
              <Button className="flex-1" disabled={busy} onClick={send} data-testid="composer-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{t("Confirm sending")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
